import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  BanknotesIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  CpuChipIcon,
  ScaleIcon,
  BellAlertIcon,
} from '@heroicons/react/24/outline';
import MainLayout from '../../layouts/MainLayout';
import userStore from '../../store/userStore';
import api, { getErrorMessage } from '../../services/api';
import { useEndpoint } from './components/ui';
import DineroSection from './components/sections/DineroSection';
import RendimientoSection from './components/sections/RendimientoSection';
import GestionSection from './components/sections/GestionSection';
import IASection from './components/sections/IASection';
import MargenSection from './components/sections/MargenSection';
import AlertasSection from './components/sections/AlertasSection';

/**
 * Rendimiento de cartera (solo admin): el panel que sustituye a Metabase, por
 * secciones. Plan: docs/panel-rendimiento-cartera-plan.md (fase 2).
 *
 * Cliente, periodo y sección viven en la URL (`?cliente=&desde=&hasta=&s=`):
 * se puede compartir un enlace y recargar sin perder lo elegido. Solo se piden
 * los datos de la sección abierta, y cada gráfica por su cuenta: un fallo en una
 * no deja en blanco las demás.
 */
export const SECCIONES = [
  { id: 'dinero', label: 'Dinero', icon: BanknotesIcon },
  { id: 'rendimiento', label: 'Rendimiento de cartera', icon: ChartBarIcon },
  { id: 'gestion', label: 'Gestión', icon: ChatBubbleLeftRightIcon },
  { id: 'ia', label: 'Agente de IA', icon: CpuChipIcon },
  { id: 'margen', label: 'Margen', icon: ScaleIcon },
  { id: 'alertas', label: 'Alertas', icon: BellAlertIcon },
];

const TODOS = 'all';

/** El valor del selector → los `user_id` a pedir. `null` = toda la cartera; `undefined` = aún no se sabe. */
export const clientIdsFor = (value, opciones) => {
  if (!value || value === TODOS) return null;
  if (value.startsWith('g:')) {
    if (!opciones.cargadas) return undefined;
    const grupo = opciones.grupos.find(g => g.id === value.slice(2));
    return grupo ? grupo.client_ids : null;
  }
  return [value.slice(2)];
};

const mesKey = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

/** Los últimos `n` meses, el actual incluido. */
export const ultimosMeses = (n, hoy = new Date()) => ({
  desde: mesKey(new Date(hoy.getFullYear(), hoy.getMonth() - (n - 1), 1)),
  hasta: mesKey(hoy),
});

/** El periodo en las formas que piden los distintos endpoints. */
export function periodo(desde, hasta) {
  const [y1, m1] = desde.split('-').map(Number);
  const [y2, m2] = hasta.split('-').map(Number);
  const inicio = new Date(y1, m1 - 1, 1);
  const fin = new Date(y2, m2, 0);
  const iso = d =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const hoy = new Date();
  const hastaHoy = fin > hoy ? hoy : fin;
  return {
    from: desde,
    to: hasta,
    desde: iso(inicio),
    hasta: iso(fin),
    dias: Math.max(1, Math.round((hastaHoy - inicio) / 86400000) + 1),
  };
}

const PRESETS = [
  { meses: 3, label: '3 meses' },
  { meses: 6, label: '6 meses' },
  { meses: 12, label: '12 meses' },
];

const PortfolioPerformancePage = () => {
  const user = userStore(state => state.user);
  const esAdmin = user?.role === 'admin';
  const [sp, setSp] = useSearchParams();
  const [opciones, setOpciones] = useState({ clientes: [], grupos: [], cargadas: false });

  const def = ultimosMeses(12);
  const seccion = SECCIONES.some(s => s.id === sp.get('s')) ? sp.get('s') : 'dinero';
  const cliente = sp.get('cliente') || TODOS;
  const desde = /^\d{4}-\d{2}$/.test(sp.get('desde') || '') ? sp.get('desde') : def.desde;
  const hasta = /^\d{4}-\d{2}$/.test(sp.get('hasta') || '') ? sp.get('hasta') : def.hasta;

  const cambiar = cambios => {
    const next = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(cambios)) {
      if (v == null || v === '') next.delete(k);
      else next.set(k, v);
    }
    setSp(next, { replace: true });
  };

  useEffect(() => {
    if (!esAdmin) return;
    api
      .get('/portfolio-performance/clients')
      .then(({ data }) => setOpciones({ ...(data.data || { clientes: [], grupos: [] }), cargadas: true }))
      .catch(err => {
        setOpciones(o => ({ ...o, cargadas: true }));
        toast.error(getErrorMessage(err));
      });
  }, [esAdmin]);

  const ids = clientIdsFor(cliente, opciones);
  const idsKey = ids ? ids.join(',') : '';
  const params = useMemo(
    () => ({ ...(idsKey ? { clients: idsKey } : {}), from: desde, to: hasta }),
    [idsKey, desde, hasta]
  );
  const per = useMemo(() => periodo(desde, hasta), [desde, hasta]);
  const singleClient = ids && ids.length === 1 ? ids[0] : null;
  // Con un grupo, hasta que llegan las opciones no se sabe qué cuentas pedir:
  // mejor esperar que enseñar la cartera entera como si fuera la del grupo.
  const listo = ids !== undefined;

  // La facturación la usan Dinero y Margen: se pide una vez y la comparten.
  const billing = useEndpoint('/portfolio-performance/billing', params, {
    enabled: esAdmin && listo && (seccion === 'dinero' || seccion === 'margen'),
  });

  if (!esAdmin) {
    return <Navigate to='/dashboard' replace />;
  }

  const nombreCliente =
    cliente === TODOS
      ? 'Toda la cartera'
      : cliente.startsWith('g:')
        ? opciones.grupos.find(g => g.id === cliente.slice(2))?.nombre
        : opciones.clientes.find(c => c.id === cliente.slice(2))?.nombre;

  const Seccion = {
    dinero: DineroSection,
    rendimiento: RendimientoSection,
    gestion: GestionSection,
    ia: IASection,
    margen: MargenSection,
    alertas: AlertasSection,
  }[seccion];

  return (
    <MainLayout>
      <div className='space-y-4'>
        <header className='bg-white border border-slate-200 rounded-lg px-5 pt-4'>
          <div className='flex flex-wrap items-end justify-between gap-3'>
            <div>
              <h1 className='text-2xl font-semibold text-slate-900'>Rendimiento de cartera</h1>
              <p className='text-sm text-slate-500 mt-1'>
                {nombreCliente || '…'} · de {per.desde.split('-').reverse().join('/')} a{' '}
                {per.hasta.split('-').reverse().join('/')}
              </p>
            </div>
            <div className='flex flex-wrap items-end gap-3'>
              <label className='text-sm text-slate-600 flex flex-col gap-1'>
                Cliente
                <select
                  aria-label='Cliente'
                  value={cliente}
                  onChange={e => cambiar({ cliente: e.target.value === TODOS ? null : e.target.value })}
                  className='form-select rounded-lg border-slate-300 text-sm w-72'
                >
                  <option value={TODOS}>Todos</option>
                  {opciones.grupos.length > 0 && (
                    <optgroup label='Grupos de cuentas'>
                      {opciones.grupos.map(g => (
                        <option key={g.id} value={`g:${g.id}`}>
                          {g.nombre} ({g.expedientes})
                        </option>
                      ))}
                    </optgroup>
                  )}
                  <optgroup label='Clientes'>
                    {opciones.clientes.map(cl => (
                      <option key={cl.id} value={`c:${cl.id}`}>
                        {cl.nombre} ({cl.expedientes})
                      </option>
                    ))}
                  </optgroup>
                </select>
              </label>
              <div className='flex flex-col gap-1'>
                <span className='text-sm text-slate-600'>Periodo</span>
                <div className='flex items-center gap-1'>
                  {PRESETS.map(p => {
                    const r = ultimosMeses(p.meses);
                    const activo = r.desde === desde && r.hasta === hasta;
                    return (
                      <button
                        key={p.meses}
                        type='button'
                        onClick={() => cambiar({ desde: p.meses === 12 ? null : r.desde, hasta: null })}
                        aria-pressed={activo}
                        className={`text-sm px-2.5 py-1.5 rounded-lg border ${
                          activo
                            ? 'border-blue-600 bg-blue-50 text-blue-700'
                            : 'border-slate-300 text-slate-600 hover:text-slate-900'
                        }`}
                      >
                        {p.label}
                      </button>
                    );
                  })}
                  <input
                    type='month'
                    aria-label='Desde'
                    value={desde}
                    max={hasta}
                    onChange={e => e.target.value && cambiar({ desde: e.target.value })}
                    className='form-input rounded-lg border-slate-300 text-sm py-1.5 w-44'
                  />
                  <span className='text-slate-400 text-sm'>a</span>
                  <input
                    type='month'
                    aria-label='Hasta'
                    value={hasta}
                    min={desde}
                    onChange={e => e.target.value && cambiar({ hasta: e.target.value })}
                    className='form-input rounded-lg border-slate-300 text-sm py-1.5 w-44'
                  />
                </div>
              </div>
            </div>
          </div>
          <nav className='flex gap-1 mt-4 -mb-px overflow-x-auto' role='tablist' aria-label='Secciones'>
            {SECCIONES.map(s => {
              const Icon = s.icon;
              const activa = s.id === seccion;
              return (
                <button
                  key={s.id}
                  type='button'
                  role='tab'
                  aria-selected={activa}
                  onClick={() => cambiar({ s: s.id === 'dinero' ? null : s.id })}
                  className={`flex items-center gap-1.5 whitespace-nowrap px-3 py-2 text-sm border-b-2 ${
                    activa
                      ? 'border-blue-600 text-blue-700 font-medium'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  <Icon className='w-4 h-4' />
                  {s.label}
                </button>
              );
            })}
          </nav>
        </header>

        {listo ? (
          <Seccion params={params} period={per} billing={billing} singleClient={singleClient} />
        ) : (
          <p className='text-sm text-slate-400 py-10 text-center'>Cargando los clientes…</p>
        )}
      </div>
    </MainLayout>
  );
};

export default PortfolioPerformancePage;
